// -----------------------------------------------------------------------------
// UI KIT — small shared building blocks. Styles live in public/styles.css (same
// class names). Keep pages free of one-off styling so the look stays consistent:
// 32px buttons, DD/Mmm/YY dates, status colours (Closed = green), grey (i) icons.
// -----------------------------------------------------------------------------
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { currentToday, formatDate, parseDateLoose } from "../format";
import { useEsc } from "../esc";
import { answer, useAsk, useLeaveGuard } from "../leaveGuard";
import type { Discipline, IssueStatus } from "../api/types";
import { Icon, type IconName } from "./icons";

export const Loading = () => <div className="muted pad">Loading…</div>;
export const ErrorBox = ({ message }: { message: string }) => <div className="error-box" role="alert">{message}</div>;

export function PageHeader({ crumb, title, actions, sub, right }: { crumb: ReactNode; title: ReactNode; actions?: ReactNode; sub?: ReactNode; right?: ReactNode }) {
  return (
    <header className="page-header">
      <div className="crumb-row"><p className="eyebrow">{crumb}</p><div className="actions">{actions}</div></div>
      <div className="title-row"><h1>{title}</h1>{right}</div>
      {sub && <p className="muted sub">{sub}</p>}
    </header>
  );
}

export const CodeChip = ({ code }: { code: string }) => <span className="code-chip">{code}</span>;

export const STATUS_CLASS: Record<IssueStatus, string> = { Open: "p-open", Critical: "p-crit", Closed: "p-closed", Voided: "p-void" };
export const StatusPill = ({ status }: { status: IssueStatus }) => <span className={`pill ${STATUS_CLASS[status]}`}>{status}</span>;

export function DiscTag({ code, disciplines }: { code: string; disciplines: Discipline[] }) {
  const d = disciplines.find((x) => x.code === code);
  return <span className="disc-tag" style={{ color: d?.colour }}>{code}</span>;
}

/** Tooltip text: one string, or several points shown as bullets (working rule 03/Oct/26). */
export type Tip = string | string[];
export const tipText = (t: Tip): string => (Array.isArray(t) ? t.join(" ") : t);

/**
 * Floating bubble for (i) icons and sidebar icon names. It is drawn on top of the whole page
 * (portal into <body>, position: fixed), so it never pushes the layout or gets cut off by a
 * scrolling table or card (comment #20). It flips left / up near the window edges.
 */
export function useFloatingTip(content: ReactNode, opts: { side?: "below" | "right"; className?: string } = {}) {
  const anchor = useRef<HTMLElement | null>(null);
  const bubble = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    if (!open || !anchor.current || !bubble.current) return;
    const a = anchor.current.getBoundingClientRect(), b = bubble.current.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight, gap = 6;
    let left = opts.side === "right" ? a.right + 10 : a.left - 6;
    let top = opts.side === "right" ? a.top + a.height / 2 - b.height / 2 : a.bottom + gap;
    if (left + b.width > vw - 8) left = Math.max(8, (opts.side === "right" ? a.left - 10 - b.width : a.right + 6 - b.width));
    if (top + b.height > vh - 8) top = Math.max(8, a.top - gap - b.height);
    setPos({ left, top });
  }, [open, opts.side]);
  const handlers = {
    onMouseEnter: () => setOpen(true), onMouseLeave: () => { setOpen(false); setPos(null); },
    onFocus: () => setOpen(true), onBlur: () => { setOpen(false); setPos(null); },
  };
  const portal = open && typeof document !== "undefined"
    ? createPortal(<div ref={bubble} role="tooltip" className={`tip-pop ${opts.className ?? ""}`}
        style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999 }}>{content}</div>, document.body)
    : null;
  return { anchor, handlers, portal };
}

/**
 * Help text lives in grey (i) icons (working rule 02/Oct/26) — one style everywhere.
 * Hover or keyboard focus shows the text; a list is shown as bullet points.
 */
// Round 31 #7: icons named in tooltip text ("🗑 deletes…", "↑ / ↓ move…") show as the same Material icons as the buttons.
const TIP_GLYPH: Record<string, IconName> = { "🗑": "delete", "▾": "arrow_drop_down", "▸": "chevron_right", "✓": "check", "⇪": "file_upload", "⇩": "file_download",
  "✎": "edit", "🔒": "lock", "📅": "calendar_today", "👥": "group", "📍": "place", "☆": "star_border", "★": "star", "⤢": "open_in_full", "📎": "attach_file",
  "📄": "picture_as_pdf", "⟲": "rotate_left", "⟳": "rotate_right", "⚙": "settings", "🔔": "notifications", "⧉": "content_copy", "⚠": "warning",
  "×": "close", "↑": "arrow_upward", "↓": "arrow_downward", "↕": "unfold_more", "‹": "chevron_left", "›": "chevron_right" };
const TIP_RE = new RegExp(`(${Object.keys(TIP_GLYPH).map((g) => g.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "u");
/** "🗑 removes…" → [<Icon delete/>, " removes…"]; text without icon glyphs is returned as it is. */
export function withIcons(text: string): ReactNode {
  if (!TIP_RE.test(text)) return text;
  return text.split(TIP_RE).map((part, k) => (TIP_GLYPH[part] ? <Icon key={k} name={TIP_GLYPH[part]} /> : part));
}

export function InfoIcon({ tip, content }: { tip: Tip; align?: "left" | "right"; /** richer bubble (e.g. the A3.3 sign-off key with chips); `tip` stays the spoken text */ content?: ReactNode }) {
  const body = content ?? (Array.isArray(tip) ? <ul>{tip.map((t, k) => <li key={k}>{withIcons(t)}</li>)}</ul> : withIcons(tip));
  const { anchor, handlers, portal } = useFloatingTip(body);
  return <><span ref={(el) => { anchor.current = el; }} className="info" tabIndex={0} role="img" aria-label={tipText(tip)} data-info="1" {...handlers}><Icon name="info" /></span>{portal}</>;
}

/** Search box with a grey × that clears it; the × shows only when there is text (comments #10, #27) and sits dead centre (round 13 #11). */
export function SearchBox({ value, onChange, placeholder, width, autoFocus, onKeyDown }: { value: string; onChange: (v: string) => void; placeholder: string; width?: number; autoFocus?: boolean; onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <span className="search-box" style={width ? { width } : undefined}>
      <input ref={input} autoFocus={autoFocus} className="search" placeholder={placeholder} aria-label={placeholder} value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={onKeyDown} />
      {value && <button type="button" className="search-x" aria-label="Clear search" title="Clear search" data-testid="search-x"
        onClick={() => { onChange(""); input.current?.focus(); }}><svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true"><path d="M1 1l6 6M7 1L1 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg></button>}
    </span>
  );
}

/** A date field's calendar is drawn in <body>; a click in it belongs to the field, not "outside" its menu or pop-up. */
const inCalendar = (e: MouseEvent) => !!(e.target as Element | null)?.closest?.("[data-calendar-pop]");

/** Calls onOutside when the user clicks outside `ref` (menus close on outside click — code list #1). */
export function useOutside(ref: RefObject<HTMLElement | null>, onOutside: () => void, active = true): void {
  const cb = useRef(onOutside);
  cb.current = onOutside;
  useEffect(() => {
    if (!active) return;
    const h = (e: MouseEvent) => { if (inCalendar(e)) return; if (ref.current && !ref.current.contains(e.target as Node)) cb.current(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [ref, active]);
}

/** Calls onOutside when a click lands outside ALL of `refs` (a button + its floating menu). */
export function useOutsideAll(refs: RefObject<HTMLElement | null>[], onOutside: () => void, active = true): void {
  const cb = useRef(onOutside);
  cb.current = onOutside;
  useEffect(() => {
    if (!active) return;
    const h = (e: MouseEvent) => { if (inCalendar(e)) return; if (refs.every((r) => !r.current || !r.current.contains(e.target as Node))) cb.current(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [active]);   // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * A sidebar menu (project switcher, profile) drawn on top of the page (portal, position: fixed), so the
 * sidebar can scroll its own items without cutting the menu off (round 15 #1).
 *   side "below": under the anchor, left edges lined up · "above": over the anchor · "right": beside it.
 */
export function SideMenu({ anchor, open, side, className, menuRef, children, onKeyDown }: {
  anchor: RefObject<HTMLElement | null>; open: boolean; side: "below" | "above" | "right"; className: string;
  menuRef: RefObject<HTMLDivElement | null>; children: ReactNode; onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
}) {
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    if (!open || !anchor.current || !menuRef.current) { setPos(null); return; }
    const a = anchor.current.getBoundingClientRect(), m = menuRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    let left = side === "right" ? a.right + 8 : a.left;
    let top = side === "below" ? a.bottom + 4 : side === "above" ? a.top - 4 - m.height : (className.includes("profile") ? a.bottom - m.height : a.top);
    top = Math.min(Math.max(8, top), vh - 8 - m.height);
    left = Math.max(8, left);
    setPos({ left, top });
  }, [open, side]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  return createPortal(<div ref={menuRef} className={`menu ${className} side-floating`} onKeyDown={onKeyDown}
    style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999 }}>{children}</div>, document.body);
}

const menuItems = (el: HTMLElement | null) =>
  el ? Array.from(el.querySelectorAll<HTMLElement>(".menu-item:not([disabled])")) : [];

/** Put keyboard focus on the chosen item of a menu (or the first one) when it opens. */
export function focusMenu(el: HTMLElement | null): void {
  const items = menuItems(el);
  (items.find((b) => b.classList.contains("current")) ?? items[0])?.focus();
}

/**
 * ↑ / ↓ (and Home / End) move between the items of a menu; Enter picks the focused item (it is a button).
 * Used by every dropdown and menu (code list #8). Not while typing in the menu's search box, except ↓
 * which jumps from the search box into the list.
 */
export function menuKeys(e: KeyboardEvent<HTMLElement>): void {
  const items = menuItems(e.currentTarget);
  if (!items.length || !["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
  const typing = (e.target as HTMLElement).matches("input, textarea");
  if (typing && e.key !== "ArrowDown") return;
  e.preventDefault();
  const i = items.indexOf(document.activeElement as HTMLElement);
  const j = e.key === "Home" ? 0 : e.key === "End" ? items.length - 1
    : e.key === "ArrowDown" ? (i < 0 ? 0 : Math.min(items.length - 1, i + 1)) : Math.max(0, i - 1);
  items[j]?.focus();
}

/**
 * A button that opens a small menu. The menu floats above the whole page (portal, position: fixed), so a
 * scrolling card can never hide it; it opens upwards when there is no room below (round 13 #4).
 * Closes on outside click, Esc, or picking an item. ↑ / ↓ + Enter work inside it (#8).
 */
export function Popover({ button, children, className = "", align = "left", label }: {
  button: (open: boolean) => ReactNode; children: (close: () => void) => ReactNode; className?: string; align?: "left" | "right"; label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const close = () => { setOpen(false); setPos(null); };
  useOutsideAll([box, menu], close, open);
  useEsc("popup", () => { if (!open) return false; close(); btn.current?.focus(); return true; }, open);
  const place = () => {
    if (!btn.current || !menu.current) return;
    const a = btn.current.getBoundingClientRect(), m = menu.current.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight, gap = 4;
    let left = align === "right" ? a.right - m.width : a.left;
    left = Math.min(Math.max(8, left), vw - 8 - m.width);
    let top = a.bottom + gap;
    if (top + m.height > vh - 8) top = a.top - gap - m.height >= 8 ? a.top - gap - m.height : Math.max(8, vh - 8 - m.height);
    setPos({ left, top });
  };
  useLayoutEffect(() => { if (open) { place(); focusMenu(menu.current); } }, [open]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    const on = () => place();
    window.addEventListener("scroll", on, true); window.addEventListener("resize", on);
    const ro = typeof ResizeObserver !== "undefined" && menu.current ? new ResizeObserver(on) : null;   // grows (e.g. date fields) → stays in view
    if (ro && menu.current) ro.observe(menu.current);
    return () => { window.removeEventListener("scroll", on, true); window.removeEventListener("resize", on); ro?.disconnect(); };
  }, [open]);   // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className={`popover ${className}`} ref={box}>
      <button ref={btn} type="button" className="pop-btn" aria-expanded={open} aria-label={label} onClick={() => (open ? close() : setOpen(true))}>{button(open)}</button>
      {open && createPortal(
        <div ref={menu} className="menu pop-menu floating" role="menu" aria-label={label} onKeyDown={menuKeys}
          style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999 }}>{children(close)}</div>, document.body)}
    </div>
  );
}

/** `stay`: picking it keeps the list open (e.g. "Select dates…" shows its From / To fields underneath — v041-016). */
export interface Option { value: string; label: ReactNode; stay?: boolean }

/** Filter button "All types ▾" → menu; highlighted while a value is chosen. value "" = all. */
export function FilterDropdown({ allLabel, value, options, onChange, label, extra }: {
  allLabel: string; value: string; options: Option[]; onChange: (v: string) => void; label?: string; extra?: (close: () => void) => ReactNode;
}) {
  const current = options.find((o) => o.value === value);
  return (
    <Popover label={label ?? allLabel} className={`filter${value ? " on" : ""}`}
      button={() => <span className="filter-btn">{value ? current?.label ?? value : allLabel} <Icon name="arrow_drop_down" /></span>}>
      {(close) => <>
        <button className={`menu-item${value === "" ? " current" : ""}`} onClick={() => { onChange(""); close(); }}>{allLabel}</button>
        {options.map((o) => (
          <button key={o.value} className={`menu-item${o.value === value ? " current" : ""}`} onClick={(e) => {
            onChange(o.value);
            if (!o.stay) return close();
            const menu = (e.currentTarget as HTMLElement).closest(".pop-menu");        // keep it open, cursor in the first field
            setTimeout(() => menu?.querySelector<HTMLInputElement>("input")?.focus(), 0);
          }}>{o.label}</button>
        ))}
        {extra?.(close)}
      </>}
    </Popover>
  );
}

/** Segmented control (Cards | List, 7 days | 30 | 60 | 90, List | Plan). */
export function Seg<T extends string>({ value, options, onChange, label }: { value: T; options: { value: T; label: ReactNode; disabled?: boolean; title?: string }[]; onChange: (v: T) => void; label?: string }) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" className={o.value === value ? "on" : ""} disabled={o.disabled} title={o.title}
          aria-pressed={o.value === value} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

/** Pop-up. `info` puts a grey (i) beside the title (A7.5 Add member, A7.3 Add role). */
export function Modal({ title, children, onClose, info, className = "" }: { title: string; children: ReactNode; onClose: () => void; info?: Tip; className?: string }) {
  useEsc("popup", () => { onClose(); return true; });
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${className}`} role="dialog" aria-label={title}>
        <div className="modal-head"><h2 className="row gap6">{title}{info && <InfoIcon tip={info} />}</h2><button className="btn icon" aria-label="Close" onClick={onClose}><Icon name="close" /></button></div>
        {children}
      </div>
    </div>
  );
}

/** Right-hand drawer (A4.2 Add issue). Esc / × / backdrop ask onClose (which may confirm discarding). */
export function Drawer({ title, sub, children, footer, onClose }: { title: string; sub?: ReactNode; children: ReactNode; footer: ReactNode; onClose: () => void }) {
  useEsc("popup", () => { onClose(); return true; });
  return (
    <div className="drawer-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="drawer" role="dialog" aria-label={title}>
        <div className="drawer-head">
          <div><h2>{title}</h2>{sub && <p className="muted small">{sub}</p>}</div>
          <button className="btn icon" aria-label="Close" onClick={onClose}><Icon name="close" /></button>
        </div>
        <div className="drawer-body">{children}</div>
        <div className="drawer-foot">{footer}</div>
      </aside>
    </div>
  );
}

/** "Discard changes?" confirmation (unsaved changes — code list #3). */
export function ConfirmDiscard({ onKeep, onDiscard }: { onKeep: () => void; onDiscard: () => void }) {
  useEsc("popup", () => { onKeep(); return true; });
  return (
    <div className="modal-backdrop top" onMouseDown={(e) => e.target === e.currentTarget && onKeep()}>
      <div className="modal small" role="alertdialog" aria-label="Discard changes?">
        <h2>Discard changes?</h2>
        <p className="muted">What you typed has not been saved.</p>
        <div className="modal-foot"><button className="btn" onClick={onKeep}>Keep editing</button><button className="btn danger" onClick={onDiscard}>Discard</button></div>
      </div>
    </div>
  );
}

/** "Save changes?" after editing a resolution-log entry and clicking away (comment #21). Esc = keep editing. */
export function ConfirmSave({ onSave, onDiscard, onKeep, text = "You edited this entry." }: { onSave?: () => void; onDiscard: () => void; onKeep: () => void; text?: string }) {
  useEsc("popup", () => { onKeep(); return true; });
  return (
    <div className="modal-backdrop top" onMouseDown={(e) => e.target === e.currentTarget && onKeep()}>
      <div className="modal small" role="alertdialog" aria-label="Save changes?">
        <h2>Save changes?</h2>
        <p className="muted">{text}</p>
        <div className="modal-foot"><button className="btn" onClick={onKeep}>Keep editing</button><span className="grow" />
          <button className="btn" onClick={onDiscard}>Discard</button>{onSave && <button className="btn primary" onClick={onSave}>Save</button>}</div>
      </div>
    </div>
  );
}

/**
 * Pop-up with typing (v0.4.3): × / Esc / Cancel / a click outside ask Save / Discard / Keep editing when something was
 * typed; the Back button and other page changes ask too (leaveGuard). Use `close` for every way out, render `confirm`.
 *   const { close, confirm } = useAskClose(dirty, onClose, save);
 */
export function useAskClose(dirty: boolean, onClose: () => void, save?: () => void) {
  const [asking, setAsking] = useState(false);
  useLeaveGuard(dirty);
  const close = () => (dirty ? setAsking(true) : onClose());
  const confirm = asking ? <ConfirmSave text="What you typed here has not been saved." onKeep={() => setAsking(false)}
    onDiscard={() => { setAsking(false); onClose(); }} onSave={save ? () => { setAsking(false); save(); } : undefined} /> : null;
  return { close, confirm };
}

/** The question asked before leaving a page with unsaved edits (core/leaveGuard.ts). Mounted once in App. */
export function GuardHost() {
  const ask = useAsk();
  if (!ask) return null;
  return createPortal(<ConfirmSave text="Your changes on this page have not been saved."
    onKeep={() => answer("keep")} onDiscard={() => answer("discard")} onSave={ask.canSave ? () => answer("save") : undefined} />, document.body);
}

export function Field({ label, children, info }: { label: ReactNode; children: ReactNode; info?: Tip }) {
  return <label className="field"><span className="field-label">{label}{info && <InfoIcon tip={info} />}</span>{children}</label>;
}

/**
 * Date field (round 18 #2, board A4.2.1). Shows DD/Mmm/YY, reports ISO dates.
 *   - Type digits only: 050326, 5 3 26, 5 mar 26 → 05/Mar/26 when you leave the field or press Enter (day, month, year).
 *   - 📅 opens the calendar: Monday first, ‹ › month, today outlined, the chosen day filled; Today / Clear.
 *     Keys while it is open: arrows move, Enter picks, Esc closes (only the calendar). Focus stays in the field.
 *   - Text that can't be read is outlined red and `onValidity(false)` tells the form, whose Save must stop
 *     with DATE_HINT — a typed date is never ignored silently (round 18 #3).
 */
export function DateInput({ value, onChange, required, ariaLabel, autoFocus, onKeyDown, onBlur, onValidity, onPick, invalid }: {
  value: string | null; onChange: (iso: string | null) => void; required?: boolean; ariaLabel?: string; autoFocus?: boolean;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void; onBlur?: () => void;
  onValidity?: (ok: boolean) => void; onPick?: () => void; invalid?: boolean;
}) {
  const [text, setText] = useState(value ? formatDate(value) : "");
  const [open, setOpen] = useState(false);
  const emitted = useRef<string | null>(value);
  const input = useRef<HTMLInputElement>(null);
  const wrap = useRef<HTMLSpanElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  // a new value from outside (not the one we just reported while typing) replaces the text
  useEffect(() => { if (value !== emitted.current) { emitted.current = value; setText(value ? formatDate(value) : ""); } }, [value]);
  const readable = text.trim() === "" || parseDateLoose(text) !== null;
  const valid = text.trim() === "" ? !required : readable;
  const lastOk = useRef<boolean | null>(null);
  useEffect(() => { if (lastOk.current !== readable) { lastOk.current = readable; onValidity?.(readable); } });
  const report = (iso: string | null) => { emitted.current = iso; onChange(iso); };
  const type = (t: string) => {
    setText(t);
    const iso = parseDateLoose(t);
    if (iso || t.trim() === "") report(iso);
  };
  const tidy = () => { const iso = parseDateLoose(text); if (iso) setText(formatDate(iso)); };
  const pick = (iso: string | null) => { setText(iso ? formatDate(iso) : ""); report(iso); setOpen(false); onPick?.(); };
  const [cursor, setCursor] = useState<string>(currentToday());
  const [view, setView] = useState<"days" | "months">("days");        // A4.2.2: the month name zooms out to the year
  const toggle = () => {
    if (!open) { setCursor(parseDateLoose(text) ?? value ?? currentToday()); setView("days"); }
    setOpen(!open); input.current?.focus();
  };
  useOutsideAll([wrap, pop], () => setOpen(false), open);
  const keys = (e: KeyboardEvent<HTMLInputElement>) => {
    if (open) {
      const months = view === "months";
      const step: Record<string, number> = months ? { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -3, ArrowDown: 3 } : { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
      if (e.key in step) { e.preventDefault(); setCursor(months ? addMonthsIso(cursor, step[e.key]) : addDaysIso(cursor, step[e.key])); return; }
      if (e.key === "Enter") { e.preventDefault(); if (months) setView("days"); else pick(cursor); return; }
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); if (months) setView("days"); else setOpen(false); return; }
    }
    if (e.key === "Enter") tidy();
    onKeyDown?.(e);
  };
  return (
    <span className="date-input" ref={wrap}>
      <input ref={input} className={valid && !invalid ? "" : "invalid"} aria-invalid={!valid || !!invalid} value={text} placeholder="DD/Mmm/YY"
        aria-label={ariaLabel} autoFocus={autoFocus} onKeyDown={keys}
        onBlur={() => { setOpen(false); tidy(); onBlur?.(); }}
        onChange={(e) => type(e.target.value)} />
      <button type="button" className="cal-btn" tabIndex={-1} aria-label="Open calendar" title="Pick from the calendar"
        onMouseDown={(e) => e.preventDefault()} onClick={toggle}><Icon name="calendar_today" /></button>
      {open && <CalendarPop anchor={wrap} popRef={pop} cursor={cursor} chosen={parseDateLoose(text)} onCursor={setCursor} onPick={pick} view={view} onView={setView} />}
    </span>
  );
}

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const addDaysIso = (iso: string, n: number) => new Date(new Date(`${iso}T00:00:00Z`).getTime() + n * 86400000).toISOString().slice(0, 10);
const addMonthsIso = (iso: string, n: number) => {
  const [y, m, d] = iso.split("-").map(Number);
  const first = new Date(Date.UTC(y, m - 1 + n, 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  return new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), Math.min(d, last))).toISOString().slice(0, 10);
};

/** Monday-first grid of one month (unit-tested): null = blank cell before the 1st / after the last day. */
export function monthGrid(year: number, month: number): (number | null)[] {
  const lead = (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const cells: (number | null)[] = Array(lead).fill(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7) cells.push(null);
  return cells;
}

function CalendarPop({ anchor, popRef, cursor, chosen, onCursor, onPick, view, onView }: {
  anchor: RefObject<HTMLElement | null>; popRef: RefObject<HTMLDivElement | null>; cursor: string; chosen: string | null;
  onCursor: (iso: string) => void; onPick: (iso: string | null) => void; view: "days" | "months"; onView: (v: "days" | "months") => void;
}) {
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const a = anchor.current?.getBoundingClientRect(), b = popRef.current?.getBoundingClientRect();
    if (!a || !b) return;
    let left = a.right - b.width, top = a.bottom + 6;
    if (left < 8) left = Math.min(a.left, window.innerWidth - b.width - 8);
    if (top + b.height > window.innerHeight - 8) top = Math.max(8, a.top - 6 - b.height);
    setPos({ left, top });
  }, [anchor, popRef, view]);
  const [y, m] = cursor.split("-").map(Number);
  const today = currentToday();
  const iso = (d: number) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const keep = (e: { preventDefault: () => void }) => e.preventDefault();       // focus stays in the field
  return createPortal(
    <div ref={popRef} className="cal-pop" data-calendar-pop="1" role="dialog" aria-label="Calendar" data-month={cursor.slice(0, 7)}
      style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999 }} onMouseDown={keep}>
      {view === "months" ? <>
        <div className="cal-head">
          <button type="button" className="btn icon" aria-label="Previous year" tabIndex={-1} onClick={() => onCursor(addMonthsIso(cursor, -12))}><Icon name="chevron_left" /></button>
          <b data-testid="cal-year">{y}</b>
          <button type="button" className="btn icon" aria-label="Next year" tabIndex={-1} onClick={() => onCursor(addMonthsIso(cursor, 12))}><Icon name="chevron_right" /></button>
        </div>
        <div className="cal-months" role="grid">
          {MONTH_NAMES.map((name, k) => {
            const mm = `${y}-${String(k + 1).padStart(2, "0")}`;
            return <button key={k} type="button" tabIndex={-1} data-month={k + 1} aria-label={`${name} ${y}`}
              className={`cal-month${chosen?.slice(0, 7) === mm ? " chosen" : ""}${today.slice(0, 7) === mm ? " today" : ""}${k + 1 === m ? " cursor" : ""}`}
              onClick={() => { onCursor(addMonthsIso(cursor, k + 1 - m)); onView("days"); }}>{name.slice(0, 3)}</button>;
          })}
        </div>
      </> : <>
      <div className="cal-head">
        <button type="button" className="btn icon" aria-label="Previous month" tabIndex={-1} onClick={() => onCursor(addMonthsIso(cursor, -1))}><Icon name="chevron_left" /></button>
        <button type="button" className="cal-title" tabIndex={-1} aria-label="Pick a month" title="Pick a month" onClick={() => onView("months")}>{MONTH_NAMES[m - 1]} {y}<span className="caret"><Icon name="arrow_drop_down" /></span></button>
        <button type="button" className="btn icon" aria-label="Next month" tabIndex={-1} onClick={() => onCursor(addMonthsIso(cursor, 1))}><Icon name="chevron_right" /></button>
      </div>
      <div className="cal-grid" role="grid">
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => <span key={d} className="cal-dow">{d}</span>)}
        {monthGrid(y, m).map((d, k) => d === null ? <span key={k} /> : (
          <button key={k} type="button" tabIndex={-1} data-day={d}
            className={`cal-day${iso(d) === chosen ? " chosen" : ""}${iso(d) === today ? " today" : ""}${iso(d) === cursor ? " cursor" : ""}`}
            aria-label={formatDate(iso(d))} aria-selected={iso(d) === chosen} onClick={() => onPick(iso(d))}>{d}</button>))}
      </div>
      </>}
      <div className="cal-foot">
        <span className="row gap8"><button type="button" className="link" tabIndex={-1} onClick={() => onPick(today)}>Today</button>
          <InfoIcon tip={DATE_TIP} /></span>
        <button type="button" className="link muted" tabIndex={-1} onClick={() => onPick(null)}>Clear</button>
      </div>
    </div>, document.body);
}

/** Board A4.2 tip 2 — "Date fields (i) — calendar footer". */
export const DATE_TIP = [
  "Type digits only: 100326 or 10 3 26 → 10/Mar/26 (day, month, year).",
  "Or click 📅 and pick a day; ‹ › change the month.",
  "Click the month name for the whole year; ‹ › change the year.",
  "Keys: arrows move, Enter picks, Esc closes.",
  "Today jumps to today; Clear empties the field.",
  "A date that can't be read is outlined red and Save stops.",
];

// ---------- Floating message (round 18 #1): "Saved", "Invitation sent"… float over the page, so nothing shifts ----------
type ToastItem = { id: number; text: string };
let toasts: ToastItem[] = [];
let toastId = 1;
const toastListeners = new Set<(t: ToastItem[]) => void>();
const emitToasts = () => toastListeners.forEach((l) => l(toasts));
/** Show a short confirmation for about 3 seconds. Errors are NOT toasts: they stay red, next to what needs fixing. */
export function toast(text: string, ms = 3000): void {
  const t = { id: toastId++, text };
  toasts = [...toasts.slice(-2), t]; emitToasts();
  setTimeout(() => { toasts = toasts.filter((x) => x !== t); emitToasts(); }, ms);
}
export function ToastHost() {
  const [list, setList] = useState<ToastItem[]>(toasts);
  useEffect(() => { toastListeners.add(setList); return () => { toastListeners.delete(setList); }; }, []);
  if (!list.length) return null;
  return createPortal(<div className="toasts" role="status" aria-live="polite">
    {list.map((t) => <div key={t.id} className="toast" data-testid="toast"><span className="toast-tick"><Icon name="check" /></span>{t.text}</div>)}
  </div>, document.body);
}

/** Round "+" button, bottom right (add issue — code list #7). */
export const Fab = ({ onClick, label }: { onClick: () => void; label: string }) =>
  <button className="fab" aria-label={label} title={label} onClick={onClick}>+</button>;

/** Sortable table header: ↕ grey when not sorted, ↑ / ↓ on the sorted column (round 13 #1). */
export function SortTh({ label, arrow, onClick, className }: { label: string; arrow: "↑" | "↓" | "↕"; onClick: () => void; className?: string }) {
  const sorted = arrow !== "↕";
  return (
    <th className={className} aria-sort={!sorted ? "none" : arrow === "↑" ? "ascending" : "descending"}>
      <button type="button" className={`sort${sorted ? " on" : ""}`} onClick={onClick} title={`Sort by ${label}`} aria-label={`Sort by ${label}`}>{label} <span className="arrow"><Icon name={arrow === "↑" ? "arrow_upward" : arrow === "↓" ? "arrow_downward" : "unfold_more"} /></span></button>
    </th>
  );
}

/** A card whose title collapses / expands it (A2, round 13 #2). Remembered on this PC. */
export function useCollapsed(key: string): [boolean, () => void] {
  const k = `buildnexx-collapsed-${key}`;
  const [c, setC] = useState(() => { try { return localStorage.getItem(k) === "1"; } catch { return false; } });
  const toggle = () => setC((v) => { try { localStorage.setItem(k, v ? "0" : "1"); } catch { /* private window */ } return !v; });
  return [c, toggle];
}

/** Square icon button whose name floats on hover, like the collapsed sidebar (round 13 #6). */
export function IconButton({ icon, name, hint, onClick, disabled }: { icon: ReactNode; name: string; hint?: string; onClick?: () => void; disabled?: boolean }) {
  const { anchor, handlers, portal } = useFloatingTip(hint ? `${name} · ${hint}` : name, { className: "name" });
  return <>
    <span ref={(el) => { anchor.current = el; }} className="icon-btn-wrap" {...handlers}>
      <button type="button" className="btn icon" aria-label={name} disabled={disabled} onClick={onClick}>{icon}</button>
    </span>{portal}
  </>;
}

/** On / off switch (A7.1 Sign-off and Project setup, A7.4 Can sign in). */
export function Switch({ on, onChange, label, disabled }: { on: boolean; onChange?: (v: boolean) => void; label: string; disabled?: boolean }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled}
    className={`switch${on ? " on" : ""}`} onClick={() => onChange?.(!on)}><Icon name={on ? "toggle_on" : "toggle_off"} /></button>;
}
